import { createAsyncThunk } from '@reduxjs/toolkit';
import { initialiseFeatureFlagsFromGrowthBook } from 'src/featureFlags/utils';
import { getCurrentUser } from 'src/models/lmsConfig/queries';
import axios from 'src/workspace/axios';
import { encrypt } from 'src/workspace/utils';
import { AUTH_REDIRECT_ENDPOINTS } from './authRedirection/constants';
import { getTransformedRevyreActivities } from './queries';
import {
  setCalendarActivityDetails,
  setCalendarActivityList,
  setCalendarData,
  setHelloCardData,
  setHelloCardPageData,
  setLeadDetails,
  setLoading,
  setWorkflowHelloCards,
} from './slice';

const isRevyreMock = localStorage.getItem('isRevyreMock') === 'true';

const url = isRevyreMock
  ? 'https://a556d0d3-4027-4273-ae26-c6bfb566a903.mock.pstmn.io'
  : '';

export const BASE_URL = `${url}/vymo/v3/workflow`;

export const logoutAuthRedirection = async () => {
  localStorage.clear();
  await axios.get(AUTH_REDIRECT_ENDPOINTS.logout);
  window.location.href = '/';
};

export const checkAuthRedirectionValidity = async (
  clientId?: string,
  accessToken?: string,
) => {
  if (!clientId || !accessToken) {
    throw new Error(
      `client_id=${clientId} or auth_token=${accessToken} missing`,
    );
  }

  const response: any = await axios({
    url: AUTH_REDIRECT_ENDPOINTS.authByToken,
    method: 'post',
    data: {
      type: 'EXTERNAL_AUTH',
      client: clientId,
      access_token: accessToken,
    },
  });

  const { data } = response;

  if (data.authentication_error) {
    throw new Error(data.authentication_error);
  }

  const authToken =
    response.headers['x-vymo-auth-token'] || response.headers.auth_token;

  if (!authToken) {
    throw new Error("Couldn't find token to proceed");
  }

  return { authToken, response };
};

export const getAuthRedirectionToken = async (rid?: string, sid?: string) => {
  if (!rid || !sid) {
    throw new Error(`rid=${rid} or sid=${sid} missing`);
  }

  const response: any = await axios({
    url: AUTH_REDIRECT_ENDPOINTS.getAuthToken,
    method: 'post',
    data: { rid, sid },
  });

  const { data } = response;

  if (data.authentication_error) {
    throw new Error(data.authentication_error);
  }

  const { context: authToken } = data;

  if (!authToken) {
    throw new Error("Couldn't find token to proceed");
  }

  return authToken;
};

export const initAuthRedirectionSession = async (authToken: string) => {
  try {
    const response: any = await axios({
      method: 'get',
      url: AUTH_REDIRECT_ENDPOINTS.initSession,
      headers: {
        'Content-Type': 'application/json',
        'X-Vymo-Auth-Token': authToken,
      },
    });

    const data = response?.data ?? response ?? {};

    if (data?.authentication_error) {
      throw new Error(data?.authentication_error);
    }

    const { client, email, auth_token: configAuthToken } = data || {};

    if (!client) {
      throw new Error('Missing client in session init response');
    }

    localStorage.setItem('client', String(client));
    localStorage.setItem(
      'config',
      encrypt(JSON.stringify(data), `${client}_VYMO`).toString(),
    );

    if (configAuthToken) {
      localStorage.setItem(
        'auth_token',
        encrypt(String(configAuthToken), `${client}_VYMO`).toString(),
      );
    }

    initialiseFeatureFlagsFromGrowthBook({
      clientCode: client,
      userEmail: email,
    });
  } catch (error) {
    await logoutAuthRedirection();
    throw error;
  }
};

export const fetchHomeDashboardCards = createAsyncThunk(
  'homeDashboard/helloCards',
  async ({ params }: { params: Record<any, any> }, { dispatch }) => {
    try {
      dispatch(setLoading(true));

      const response: any = await axios.get('/hello/cards', { params });
      dispatch(setHelloCardData(response.data.results));
    } catch (error: any) {
      // eslint-disable-next-line
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const fetchHelloCardsForPage = createAsyncThunk(
  'homeDashboard/calendarData',
  async ({ params }: { params: Record<any, any> }, { dispatch }) => {
    try {
      dispatch(setLoading(true));

      const response: any = await axios.get('/hello/cards/page', { params });

      dispatch(
        setHelloCardPageData({
          categories: response.data.categories,
          pageCode: params.page,
        }),
      );
    } catch (error: any) {
      // eslint-disable-next-line
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const fetchHomeDashboardCalenderData = createAsyncThunk(
  'homeDashboard/helloCalendar',
  async (
    { params, type }: { params: Record<any, any>; type: 'revyre' | 'legacy' },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true));
      if (type === 'revyre') {
        const { data }: any = await axios.post(
          '/vymo/v3/workflow/activity/list',
          {
            filters: {
              scheduleFromDate: {
                start: new Date(params?.start).getTime(),
                end: new Date(params?.end).getTime(),
                type: 'custom',
              },
            },
            page: {
              pageNumber: 1,
              pageSize: 100,
            },
          },
        );
        const transformedItems = getTransformedRevyreActivities(
          data.payload?.result || [],
        );
        dispatch(setCalendarData(transformedItems));
      } else {
        const response: any = await axios.get('/cs/all', { params });
        dispatch(setCalendarData(response.data.results));
      }
    } catch (error: any) {
      // eslint-disable-next-line
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const fetchCalendarActivityList = createAsyncThunk(
  'homeDashboard/activitiesList',
  async ({ params }: { params: Record<any, any> }, { dispatch }) => {
    try {
      dispatch(setLoading(true));

      const response: any = await axios.get('/report/team-performance', {
        params,
      });
      dispatch(setCalendarActivityList(response.data[0].config.default));
    } catch (error: any) {
      // eslint-disable-next-line
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const getLeadData = createAsyncThunk(
  'homeDashboard/leadData',
  async ({ code }: { code: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true));

      const response = await axios.get('/lead', {
        params: { code },
      });

      dispatch(setLeadDetails(response.data));
    } catch (error) {
      // eslint-disable-next-line
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const getCalendarDetails = createAsyncThunk(
  'homeDashboard/calendarDetails',
  async (
    { code, type }: { code: string; type: 'revyre' | 'legacy' },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true));

      if (type === 'legacy') {
        const response = await axios.get(`/cs/calendar-items/${code}`);
        dispatch(setCalendarActivityDetails(response.data));
      } else {
        const { data } = await axios.get(`/vymo/v3/workflow/activity/${code}`);
        dispatch(setCalendarActivityDetails(data.payload));
      }
    } catch (error) {
      // eslint-disable-next-line
      console.error(error);
    } finally {
      dispatch(setLoading(false));
    }
  },
);

const getHelloCards = (payload) =>
  axios.post(`${BASE_URL}/hello/cards`, payload);

const onActivate = async ({ dispatch }) => {
  const userId = await getCurrentUser()?.code;
  const response: any = await getHelloCards({ userId });

  dispatch(setWorkflowHelloCards(response.data.results));
};

const onDeactivate = () => {};

export default { onActivate, onDeactivate };
